import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { User } from '../types'
import { API_URL } from '../api'
import { getToken } from '../api'


interface AuthContextValue {
  user: User | null
  token: string | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ token: string; user: User }>
  logout: () => void
  updateUser: (updates: Partial<User>) => void
  authFetch: (url: string, options?: { headers?: Record<string, string> } & Omit<RequestInit, 'headers'>) => Promise<Response>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Load token from localStorage on mount
  useEffect(() => {
    const saved = getToken()
    const savedUser = localStorage.getItem('atlas_user')
    if (saved && savedUser) {
      setToken(saved)
      setUser(JSON.parse(savedUser))
    }
    setLoading(false)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_URL}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!res.ok) {
      throw new Error(data.error || 'Login failed')
    }
    setToken(data.token)
    setUser(data.user)
    localStorage.setItem('atlas_token', data.token)
    localStorage.setItem('atlas_user', JSON.stringify(data.user))
    return data
  }, [])

  const logout = useCallback(() => {
    // Set presence to offline before clearing token
    const savedToken = getToken()
    if (savedToken) {
      try {
        fetch(`${API_URL}/api/presence`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${savedToken}` },
          body: JSON.stringify({ status: 'offline' }),
        }).catch(() => {})
      } catch {
        // ignore
      }
    }
    setToken(null)
    setUser(null)
    localStorage.removeItem('atlas_token')
    localStorage.removeItem('atlas_user')
  }, [])

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev
      const next = { ...prev, ...updates }
      localStorage.setItem('atlas_user', JSON.stringify(next))
      return next
    })
  }, [])

  // Helper to add auth header to fetch calls — 401 handled by global interceptor
  const authFetch = useCallback((url: string, options: { headers?: Record<string, string> } & Omit<RequestInit, 'headers'> = {}) => {
    const headers = { ...options.headers }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return fetch(url, { ...options, headers })
  }, [token])

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, authFetch }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
